import React, { useState, useEffect, useRef } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { Button } from '@/components/ui';
import { Input, Field, FieldLabel } from '../../../components/ui';
import { paymentService, type FeePreviewResult } from '../../../services/payment';
import { useStripeElementsAppearance } from '../../../hooks/useStripeElementsAppearance';

export interface PublicBookingPaymentFormProps {
  stripePromise: Stripe | null;
  bookingId: string;
  bookingIds?: string[];
  amount: number;
  /**
   * Subtotal explanation before fees (e.g. pro-rated sessions, multi camp days).
   */
  priceSubtotalLabel?: string;
  customerEmail: string;
  isAuthenticated?: boolean;
  onSuccess: () => void;
  onError: (error: string) => void;
}

/** Must render inside <Elements options={{ clientSecret }}> — confirms existing PI (matches Connect on_behalf_of). */
function PublicBookingPaymentConfirm({
  customerEmail,
  isAuthenticated,
  effectiveAmount,
  formatMoney,
  onSuccess,
  onError,
}: {
  customerEmail: string;
  isAuthenticated: boolean;
  effectiveAmount: number;
  formatMoney: (n: number) => string;
  onSuccess: () => void;
  onError: (error: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please try again.');
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message || 'Please check your payment details.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: paymentError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}${window.location.pathname}${window.location.search}`,
          payment_method_data: {
            billing_details: {
              email: customerEmail,
            },
          },
        },
        redirect: 'if_required',
      });

      if (paymentError) {
        setError(paymentError.message || 'Payment failed');
        onError(paymentError.message || 'Payment failed');
      } else if (paymentIntent?.status === 'succeeded') {
        if (isAuthenticated && paymentIntent.id) {
          try {
            await paymentService.confirmLessonPayment(paymentIntent.id);
          } catch (err) {
            console.warn('Confirm payment callback failed (webhook may have run):', err);
          }
        }
        onSuccess();
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to process payment';
      setError(message);
      onError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-foreground">Payment Details</label>
        </div>
        <div className="rounded-md border border-border p-4">
          <PaymentElement options={{ layout: 'tabs' }} />
        </div>
        {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
      </div>

      <Button
        type="submit"
        disabled={!stripe || loading}
        className="w-full"
        size="lg"
        isLoading={loading}
      >
        {loading ? 'Processing...' : `Pay ${formatMoney(effectiveAmount)}`}
      </Button>
    </form>
  );
}

export function PublicBookingPaymentForm({
  stripePromise,
  bookingId,
  bookingIds,
  amount,
  priceSubtotalLabel,
  customerEmail,
  isAuthenticated = false,
  onSuccess,
  onError,
}: PublicBookingPaymentFormProps) {
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const { appearance, isDark: stripeDarkMode } = useStripeElementsAppearance();
  const [hasCouponCode, setHasCouponCode] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; amountOff: number } | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [feePreview, setFeePreview] = useState<FeePreviewResult | null>(null);
  const [feePreviewLoading, setFeePreviewLoading] = useState(true);

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [intentLoading, setIntentLoading] = useState(false);
  const [intentError, setIntentError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setFeePreviewLoading(true);
      try {
        const res = await paymentService.getFeePreview(bookingId, appliedCoupon?.code ?? undefined, bookingIds);
        if (!cancelled && res.data) setFeePreview(res.data);
        else if (!cancelled) setFeePreview(null);
      } catch {
        if (!cancelled) setFeePreview(null);
      } finally {
        if (!cancelled) setFeePreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookingId, appliedCoupon?.code, bookingIds]);

  // Create PaymentIntent on the server first, then bind Elements with clientSecret.
  // Deferred mode (mode/amount only) is incompatible with Connect destination charges (on_behalf_of).
  useEffect(() => {
    if (!stripePromise || feePreviewLoading) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setIntentLoading(true);
      setIntentError(null);
      setClientSecret(null);
      try {
        const paymentIntentResponse = await paymentService.createLessonPayment(bookingId, appliedCoupon?.code, bookingIds);
        if (cancelled) return;
        const secret = paymentIntentResponse.data?.clientSecret;
        if (secret) setClientSecret(secret);
        else setIntentError('Could not start payment. Please try again.');
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to start payment';
          setIntentError(message);
          onErrorRef.current?.(message);
        }
      } finally {
        if (!cancelled) setIntentLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bookingId, appliedCoupon?.code, feePreviewLoading, stripePromise]);

  /** Pro‑rated / per‑session style subtotal row (matches list page; amount is booking subtotal before fees). */
  const showLabeledPriceSubtotal = priceSubtotalLabel != null && priceSubtotalLabel.length > 0;
  const showBookingSubtotal = !showLabeledPriceSubtotal && amount > 0;

  const effectiveAmount =
    feePreview?.totalToCharge ??
    (appliedCoupon ? Math.max(0, Math.round((amount - appliedCoupon.amountOff) * 100) / 100) : amount);
  const currency = feePreview?.currency?.toUpperCase() || 'USD';
  const formatMoney = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setApplyingCoupon(true);
    setCouponError(null);
    try {
      const res = await paymentService.validateCoupon(bookingId, couponCode.trim());
      if (res.data?.valid && res.data?.amountOff != null) {
        setAppliedCoupon({ code: couponCode.trim(), amountOff: res.data.amountOff });
        setCouponError(null);
      } else {
        setCouponError(res.data?.error || 'Invalid or expired coupon');
        setAppliedCoupon(null);
      }
    } catch {
      setCouponError('Failed to validate coupon');
      setAppliedCoupon(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  return (
    <div className="space-y-6">
      <div
        className="space-y-2 rounded-lg border border-border bg-muted/40 p-4"
        data-testid="public-booking-coupon"
      >
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={hasCouponCode}
            onChange={(e) => {
              const checked = e.target.checked;
              setHasCouponCode(checked);
              if (!checked) {
                setCouponCode('');
                setAppliedCoupon(null);
                setCouponError(null);
              }
            }}
            className="rounded border-input text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-foreground">
            I have a coupon code
          </span>
        </label>
        {hasCouponCode && (
          <>
            <div className="flex gap-2 pt-1">
              <Field className="flex-1">
                <FieldLabel htmlFor="public-booking-coupon-code" className="sr-only">Coupon code</FieldLabel>
                <Input id="public-booking-coupon-code" type="text" value={couponCode} onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(null); }} placeholder="Enter code" disabled={!!appliedCoupon} className="flex-1 uppercase" />
              </Field>
              {!appliedCoupon ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleApplyCoupon}
                  disabled={applyingCoupon || !couponCode.trim()}
                >
                  {applyingCoupon ? 'Checking...' : 'Apply'}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setAppliedCoupon(null);
                    setCouponCode('');
                    setCouponError(null);
                  }}
                >
                  Remove
                </Button>
              )}
            </div>
            {couponError && (
              <p className="text-sm text-red-600 dark:text-red-400">{couponError}</p>
            )}
            {appliedCoupon && (
              <p className="text-sm text-green-600 dark:text-green-400">
                Discount: −${appliedCoupon.amountOff.toFixed(2)} applied
              </p>
            )}
          </>
        )}
      </div>

      <div className="space-y-1 rounded-md bg-muted/40 p-4">
        {showLabeledPriceSubtotal && (
          <div className="flex justify-between text-sm text-foreground">
            <span>{priceSubtotalLabel}</span>
            <span>{formatMoney(amount)}</span>
          </div>
        )}
        {showBookingSubtotal && (
          <div className="flex justify-between text-sm text-foreground">
            <span>Lesson price:</span>
            <span>{formatMoney(amount)}</span>
          </div>
        )}
        {appliedCoupon && (
          <div className="flex justify-between text-sm text-green-700 dark:text-green-400">
            <span>Promo code:</span>
            <span>−{formatMoney(appliedCoupon.amountOff)}</span>
          </div>
        )}
        {feePreview && feePreview.processingFee > 0 && (
          <div className="flex justify-between text-sm text-foreground">
            <span>Processing fee:</span>
            <span>{formatMoney(feePreview.processingFee)}</span>
          </div>
        )}
        {feePreview && feePreview.taxAmount > 0 && (
          <div className="flex justify-between text-sm text-foreground">
            <span>{feePreview.taxInclusive ? 'Includes tax:' : 'Tax:'}</span>
            <span>{formatMoney(feePreview.taxAmount)}</span>
          </div>
        )}
        <div className="flex items-center justify-between border-t border-border pt-1">
          <span className="text-lg font-semibold text-foreground">Total Amount:</span>
          <span className="text-2xl font-bold text-green-600">
            {feePreviewLoading ? '…' : formatMoney(effectiveAmount)}
          </span>
        </div>
      </div>

      {(feePreviewLoading || intentLoading) && (
        <div className="py-6 text-center text-sm text-muted-foreground">Preparing secure checkout…</div>
      )}
      {intentError && (
        <p className="text-center text-sm text-destructive">{intentError}</p>
      )}

      {stripePromise && clientSecret && (
        <Elements
          key={`${clientSecret}-${stripeDarkMode ? 'dark' : 'light'}`}
          stripe={stripePromise}
          options={{ clientSecret, appearance }}
        >
          <PublicBookingPaymentConfirm
            customerEmail={customerEmail}
            isAuthenticated={isAuthenticated}
            effectiveAmount={effectiveAmount}
            formatMoney={formatMoney}
            onSuccess={onSuccess}
            onError={onError}
          />
        </Elements>
      )}

    </div>
  );
}
